import { Loading } from '@/components';
import { useCheckRemoveNotExistUserIdOrNotExistOrganizationId } from '@/features/project/_hooks/form-control';
import { useProject, useSplitProject } from '@/features/project/_hooks/project';
import { ProjectForm } from '@/features/project/projects/forms/_components/ProjectForm';
import type {
  KsFormSchema,
  ProjectFormSchema,
  RsFormSchema,
  SsFormSchema,
} from '@/features/project/projects/forms/_model/project-form';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { z } from 'zod';
import { useUpsertFormInitialValue } from '../_hooks/initial-value';

export const SplitProjectPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();
  const { id } = useId();
  const project = useProject(id);
  const {
    openUpdateConfirmModal,
    onCancelModal,
    checkRemoveNotExistUserIdOrNotExistOrganizationId,
  } = useCheckRemoveNotExistUserIdOrNotExistOrganizationId();

  const { mutateAsync } = useSplitProject();

  const {
    initialSnapshot,
    organizationIds,
    nestedRelatedOrganizationIds,
    nestedOtherResponsibleOrganizationIds,
    policiesForProject,
    fundAdditionalAttributes,
    independentAdministrativeAgencyIds,
    expenditureBudgetPurpose,
    isInitialDataFetchedLoading,
  } = useUpsertFormInitialValue(project.data);

  if (!project.data || isInitialDataFetchedLoading) {
    return <Loading />;
  }

  const onSplitProject = async (values: z.infer<typeof ProjectFormSchema>) => {
    const { newSplitProjectId } = await mutateAsync({ id, ...values });

    openMessage('success', t('message-update-completed'));
    navigate(`/rs/project/${newSplitProjectId}`);
  };

  return (
    <>
      <ProjectForm
        type="edit"
        isSubmitEnabledOnMount
        openUpdateConfirmModal={openUpdateConfirmModal}
        onCancelModal={onCancelModal}
        onUpdateProject={onSplitProject}
        initialValues={
          project.data.sheetType === 'RS'
            ? {
                sheetType: 'RS',
                data: {
                  // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                  ...(project.data as unknown as z.infer<
                    typeof RsFormSchema
                  >['data']),
                  skipAdministrativeReformTeamEvaluation:
                    project.data.skipAdministrativeReformTeamEvaluation,
                  fiscalYear: dayjs().year(project.data.fiscalYear),
                  period: [
                    dayjs().year(project.data.fiscalYear),
                    project.data.endYear
                      ? dayjs().year(project.data.endYear)
                      : undefined,
                  ],
                  organizationIds,
                  nestedRelatedOrganizationIds,
                  policiesForProject,
                  mainCostIds: project.data.mainCosts.map(
                    (mainCost) => mainCost.id,
                  ),
                  majorExpenditureBudgetPurposeIds: [
                    expenditureBudgetPurpose?.accountingId,
                    expenditureBudgetPurpose?.jurisdictionId,
                    expenditureBudgetPurpose?.accountingType.type === 'general'
                      ? expenditureBudgetPurpose.organizationId
                      : expenditureBudgetPurpose?.accountTitleId,
                    expenditureBudgetPurpose?.expenditurePurpose.upperId,
                    expenditureBudgetPurpose?.id,
                  ].flatMap((v) => v ?? []),
                  relatedProjects: [],
                  nestedOtherResponsibleOrganizationIds,
                  fiscalYearOfSnapshot: initialSnapshot,
                },
              }
            : project.data.sheetType === 'SS'
              ? {
                  sheetType: 'SS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(project.data as unknown as z.infer<
                      typeof SsFormSchema
                    >['data']),
                    fiscalYear: dayjs().year(project.data.fiscalYear),
                    period: [
                      dayjs().year(project.data.fiscalYear),
                      project.data.endYear
                        ? dayjs().year(project.data.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds,
                    independentAdministrativeAgencyIds,
                    mainCostIds: project.data.mainCosts.map(
                      (mainCost) => mainCost.id,
                    ),
                    relatedProjects: [],
                    nestedOtherResponsibleOrganizationIds,
                    fiscalYearOfSnapshot: initialSnapshot,
                  },
                }
              : {
                  sheetType: 'KS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(project.data as unknown as z.infer<
                      typeof KsFormSchema
                    >['data']),
                    fundAdditionalAttributes: fundAdditionalAttributes
                      ? {
                          ...fundAdditionalAttributes,
                          scheduledEnd: {
                            ...fundAdditionalAttributes?.scheduledEnd,
                            endDate: fundAdditionalAttributes?.scheduledEnd
                              ?.endDate
                              ? dayjs(
                                  fundAdditionalAttributes.scheduledEnd.endDate,
                                )
                              : undefined,
                            endDateOfNewApplicationReceiving:
                              fundAdditionalAttributes?.scheduledEnd
                                ?.endDateOfNewApplicationReceiving
                                ? dayjs(
                                    fundAdditionalAttributes.scheduledEnd
                                      .endDateOfNewApplicationReceiving,
                                  )
                                : undefined,
                          },
                        }
                      : null,
                    fiscalYear: dayjs().year(project.data.fiscalYear),
                    period: [
                      dayjs().year(project.data.fiscalYear),
                      project.data.endYear
                        ? dayjs().year(project.data.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds,
                    relatedProjects: [],
                    nestedOtherResponsibleOrganizationIds,
                    fiscalYearOfSnapshot: initialSnapshot,
                  },
                }
        }
        onSubmit={async (values) => {
          const { isRemovedNotExistValue } =
            await checkRemoveNotExistUserIdOrNotExistOrganizationId(values);
          if (!isRemovedNotExistValue) {
            await onSplitProject(values);
          }
        }}
      />
    </>
  );
};
